import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function CtaSection() {
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', agency: '', message: '' });
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('loading');
    try {
      const body = new URLSearchParams(formData as Record<string, string>);
      const res = await fetch('https://readdy.ai/api/form/d7cm221noju8nk5a0ugg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) {
        setStatus('success');
        setFormData({ name: '', email: '', agency: '', message: '' });
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <>
      <section className="relative py-28 overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/a203bd1fcd128c140ff693adcafdf6b9.jpg"
            alt="CTA background"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-[#0d0d0d]/80"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 text-center">
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="w-8 h-px bg-[#d4af37]"></div>
            <span className="text-[#d4af37] text-xs uppercase tracking-[3px] font-semibold">Take Action</span>
            <div className="w-8 h-px bg-[#d4af37]"></div>
          </div>
          <h2 className="text-3xl md:text-5xl lg:text-6xl font-black text-white mb-6 leading-tight">
            Ready to get accredited
            <br />
            <span className="text-[#d4af37]">the right way?</span>
          </h2>
          <p className="text-[#b0b0b0] text-lg max-w-xl mx-auto mb-10 leading-relaxed">
            Book your consultation today and take the first step toward compliance and growth.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => setShowModal(true)}
              className="whitespace-nowrap bg-[#d4af37] text-[#0d0d0d] font-bold text-sm px-10 py-4 rounded-full hover:bg-[#f4c430] transition-all duration-300 cursor-pointer"
            >
              Book Your Consultation
            </button>
            <Link
              to="/services"
              className="whitespace-nowrap border-2 border-white/30 text-white font-bold text-sm px-10 py-4 rounded-full hover:border-[#d4af37] hover:text-[#d4af37] transition-all duration-300 cursor-pointer"
            >
              View All Services
            </Link>
          </div>
        </div>
      </section>

      {/* Consultation Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <div className="bg-[#161616] border border-[#d4af37]/20 rounded-2xl w-full max-w-lg p-8 relative">
            <button
              onClick={() => { setShowModal(false); setStatus('idle'); }}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-[#888] hover:text-[#d4af37] cursor-pointer"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
            <h3 className="text-white font-black text-2xl mb-2">Book a Consultation</h3>
            <p className="text-[#888] text-sm mb-6">Fill out the form and our team will reach out within 24 hours.</p>

            {status === 'success' ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#d4af37]/20 mx-auto mb-4">
                  <i className="ri-check-line text-[#d4af37] text-3xl"></i>
                </div>
                <p className="text-white font-bold text-lg mb-2">Request Received!</p>
                <p className="text-[#888] text-sm">We&apos;ll be in touch within 24 hours.</p>
              </div>
            ) : (
              <form data-readdy-form onSubmit={handleSubmit} className="flex flex-col gap-4">
                <div>
                  <label className="text-[#888] text-xs uppercase tracking-wider mb-1.5 block">Full Name</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Jane Smith"
                    className="w-full bg-[#1e1e1e] border border-[#d4af37]/20 rounded-lg px-4 py-3 text-sm text-white placeholder-[#555] outline-none focus:border-[#d4af37]/60 transition-colors"
                  />
                </div>
                <div>
                  <label className="text-[#888] text-xs uppercase tracking-wider mb-1.5 block">Email Address</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="jane@agency.com"
                    className="w-full bg-[#1e1e1e] border border-[#d4af37]/20 rounded-lg px-4 py-3 text-sm text-white placeholder-[#555] outline-none focus:border-[#d4af37]/60 transition-colors"
                  />
                </div>
                <div>
                  <label className="text-[#888] text-xs uppercase tracking-wider mb-1.5 block">Agency Name</label>
                  <input
                    type="text"
                    name="agency"
                    value={formData.agency}
                    onChange={handleChange}
                    placeholder="Your Home Health Agency"
                    className="w-full bg-[#1e1e1e] border border-[#d4af37]/20 rounded-lg px-4 py-3 text-sm text-white placeholder-[#555] outline-none focus:border-[#d4af37]/60 transition-colors"
                  />
                </div>
                <div>
                  <label className="text-[#888] text-xs uppercase tracking-wider mb-1.5 block">Message</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={3}
                    maxLength={500}
                    placeholder="Tell us about your accreditation needs..."
                    className="w-full bg-[#1e1e1e] border border-[#d4af37]/20 rounded-lg px-4 py-3 text-sm text-white placeholder-[#555] outline-none focus:border-[#d4af37]/60 transition-colors resize-none"
                  />
                  <p className="text-[#555] text-xs mt-1 text-right">{formData.message.length}/500</p>
                </div>
                {status === 'error' && (
                  <p className="text-red-400 text-xs">Something went wrong. Please try again.</p>
                )}
                <button
                  type="submit"
                  disabled={status === 'loading'}
                  className="whitespace-nowrap bg-[#d4af37] text-[#0d0d0d] font-bold text-sm py-3.5 rounded-full hover:bg-[#f4c430] transition-colors duration-200 cursor-pointer disabled:opacity-60"
                >
                  {status === 'loading' ? 'Sending...' : 'Submit Request'}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}
